import { Button } from "@/components/ui/button";

export interface PageResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type PaginationItem = number | "ellipsis";

/** Keep first/last pages visible and expose a compact window around the current page. */
export function getPaginationItems(page: number, totalPages: number): PaginationItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  if (page <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", totalPages];
  }
  if (page >= totalPages - 3) {
    return [1, "ellipsis", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "ellipsis", page - 1, page, page + 1, "ellipsis", totalPages];
}

/** Accepts the new paged envelope while keeping legacy array endpoints usable. */
export function normalizePage<T>(
  response: T[] | object,
  property: string,
  requestedPage = 1,
  requestedPageSize = 20,
): PageResult<T> {
  const envelope = response as { [key: string]: unknown; total?: number; page?: number; pageSize?: number; totalPages?: number };
  const items = Array.isArray(response)
    ? response
    : (Array.isArray(envelope[property]) ? envelope[property] : []) as T[];
  const pageSize = !Array.isArray(response) && typeof envelope.pageSize === "number"
    ? envelope.pageSize
    : requestedPageSize;
  const total = !Array.isArray(response) && typeof envelope.total === "number"
    ? envelope.total
    : items.length;
  const page = !Array.isArray(response) && typeof envelope.page === "number"
    ? envelope.page
    : 1;
  const totalPages = !Array.isArray(response) && typeof envelope.totalPages === "number"
    ? envelope.totalPages
    : 1;
  return { items, total, page: Math.max(1, page || requestedPage), pageSize, totalPages: Math.max(1, totalPages) };
}

export function PaginationControls({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  disabled = false,
}: {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}) {
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  const items = getPaginationItems(page, totalPages);
  return (
    <nav className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4" aria-label="Pagination">
      <p className="text-sm text-muted-foreground">Showing {first}–{last} of {total}</p>
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        <Button size="sm" variant="outline" onClick={() => onPageChange(page - 1)} disabled={disabled || page <= 1}>
          Previous
        </Button>
        <div className="flex flex-wrap items-center justify-center gap-1" aria-label="Page numbers">
          {items.map((item, index) => item === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              className="flex h-8 min-w-6 items-center justify-center px-1 text-sm text-muted-foreground"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <Button
              key={item}
              size="sm"
              variant={item === page ? "default" : "outline"}
              className="h-8 min-w-8 px-2"
              aria-label={`Go to page ${item}`}
              aria-current={item === page ? "page" : undefined}
              disabled={disabled || item === page}
              onClick={() => onPageChange(item)}
            >
              {item}
            </Button>
          ))}
        </div>
        <span className="min-w-20 text-center text-sm text-muted-foreground" aria-live="polite">
          Page {page} of {totalPages}
        </span>
        <Button size="sm" variant="outline" onClick={() => onPageChange(page + 1)} disabled={disabled || page >= totalPages}>
          Next
        </Button>
      </div>
    </nav>
  );
}